/**
 * InstancedAssetManager.ts
 *
 * Core Three.js GPU Instancing engine for complex multi-mesh Fab assets.
 * Follows official Three.js instancing architecture:
 * ONE real 3D asset -> extract sub-mesh geometries + materials -> GPU InstancedMesh -> many instances.
 *
 * Preserves 100% of the original real 3D appearance by instancing each sub-mesh part
 * (body, frame, glass, panels, doors, chrome accents) with its exact local transform.
 */

import React, { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { instanceRegistry } from './InstanceRegistry';

export interface SubMeshPart {
  id: string;
  name: string;
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  localMatrix: THREE.Matrix4;
  castShadow?: boolean;
  receiveShadow?: boolean;
}

export interface InstanceTransform {
  id: string;
  position: [number, number, number];
  rotation?: [number, number, number]; // Euler [X, Y, Z] in radians
  scale?: [number, number, number];
  visible?: boolean;
  color?: THREE.ColorRepresentation;
}

const TEMP_OBJECT = new THREE.Object3D();
const TEMP_PART_MATRIX = new THREE.Matrix4();
const ZERO_SCALE_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

/**
 * Traverses any real Three.js Object3D / Group (loaded GLB/OBJ or procedural composite)
 * and extracts all child meshes with their local transform relative to the root.
 */
export function extractSubMeshes(root: THREE.Object3D): SubMeshPart[] {
  root.updateMatrixWorld(true);
  const rootInverse = root.matrixWorld.clone().invert();
  const parts: SubMeshPart[] = [];
  let partIndex = 0;

  root.traverse((child) => {
    if (child instanceof THREE.Mesh && child.geometry && child.material) {
      child.updateMatrixWorld(true);
      const relativeMatrix = child.matrixWorld.clone().premultiply(rootInverse);

      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((mat) => {
        parts.push({
          id: `part_${partIndex++}`,
          name: child.name || `mesh_${partIndex}`,
          geometry: child.geometry,
          material: mat,
          localMatrix: relativeMatrix,
          castShadow: child.castShadow,
          receiveShadow: child.receiveShadow,
        });
      });
    }
  });

  return parts;
}

export interface InstancedMultiMeshGroupProps {
  layerId: string;
  parts: readonly SubMeshPart[];
  instances: readonly InstanceTransform[];
  onSelect?: (id: string) => void;
  interactive?: boolean;
  castShadow?: boolean;
  receiveShadow?: boolean;
}

/**
 * Renders a full multi-mesh asset as a set of GPU InstancedMeshes (1 draw call per sub-mesh).
 * Exactly reconstructs the original multi-part 3D model for all N instances.
 */
export const InstancedMultiMeshGroup: React.FC<InstancedMultiMeshGroupProps> = React.memo(
  ({
    layerId,
    parts,
    instances,
    onSelect,
    interactive = true,
    castShadow = true,
    receiveShadow = true,
  }) => {
    const meshRefs = useRef<(THREE.InstancedMesh | null)[]>([]);

    // Register instance indices in InstanceRegistry
    useLayoutEffect(() => {
      instanceRegistry.clear(layerId);
      for (let i = 0; i < instances.length; i++) {
        const inst = instances[i]!;
        instanceRegistry.register(layerId, i, inst.id);
      }
    }, [layerId, instances]);

    // Precalculate instance world matrices
    const worldMatrices = useMemo(() => {
      const matrices: THREE.Matrix4[] = new Array(instances.length);
      for (let i = 0; i < instances.length; i++) {
        const inst = instances[i]!;
        if (inst.visible === false) {
          matrices[i] = ZERO_SCALE_MATRIX;
          continue;
        }
        TEMP_OBJECT.position.set(...inst.position);
        if (inst.rotation) {
          TEMP_OBJECT.rotation.set(...inst.rotation);
        } else {
          TEMP_OBJECT.rotation.set(0, 0, 0);
        }
        if (inst.scale) {
          TEMP_OBJECT.scale.set(...inst.scale);
        } else {
          TEMP_OBJECT.scale.set(1, 1, 1);
        }
        TEMP_OBJECT.updateMatrix();
        matrices[i] = TEMP_OBJECT.matrix.clone();
      }
      return matrices;
    }, [instances]);

    // Update InstancedMesh matrices for each sub-mesh part
    useLayoutEffect(() => {
      const count = instances.length;
      if (count === 0) return;

      const hasColors = instances.some((inst) => inst.color !== undefined);
      const tempColor = new THREE.Color();

      for (let pIdx = 0; pIdx < parts.length; pIdx++) {
        const mesh = meshRefs.current[pIdx];
        if (!mesh) continue;

        const part = parts[pIdx]!;
        mesh.count = count;

        for (let i = 0; i < count; i++) {
          const worldM = worldMatrices[i]!;
          if (worldM === ZERO_SCALE_MATRIX) {
            mesh.setMatrixAt(i, ZERO_SCALE_MATRIX);
          } else {
            // Compound transform: M_instance_world * M_part_local
            TEMP_PART_MATRIX.multiplyMatrices(worldM, part.localMatrix);
            mesh.setMatrixAt(i, TEMP_PART_MATRIX);
          }

          if (hasColors) {
            const c = instances[i]?.color;
            if (c) {
              tempColor.set(c);
              mesh.setColorAt(i, tempColor);
            }
          }
        }

        mesh.instanceMatrix.needsUpdate = true;
        if (hasColors && mesh.instanceColor) {
          mesh.instanceColor.needsUpdate = true;
        }
        mesh.computeBoundingSphere();
        mesh.computeBoundingBox();
      }
    }, [parts, instances, worldMatrices]);

    if (instances.length === 0 || parts.length === 0) return null;

    const handleClick = interactive && onSelect
      ? (e: any) => {
          instanceRegistry.handleClick(layerId, e, onSelect);
        }
      : undefined;

    return (
      <group name={`InstancedAssetGroup-${layerId}`}>
        {parts.map((part, pIdx) => (
          <instancedMesh
            key={`${layerId}-${part.id}-${pIdx}`}
            ref={(el) => {
              meshRefs.current[pIdx] = el;
            }}
            args={[part.geometry, part.material, instances.length]}
            material={part.material}
            castShadow={part.castShadow ?? castShadow}
            receiveShadow={part.receiveShadow ?? receiveShadow}
            onClick={handleClick}
            frustumCulled
            dispose={null}
          />
        ))}
      </group>
    );
  }
);

InstancedMultiMeshGroup.displayName = 'InstancedMultiMeshGroup';

class InstancedAssetManagerService {
  private assetRegistry = new Map<string, SubMeshPart[]>();

  public registerAsset(type: string, sourceObject: THREE.Object3D): SubMeshPart[] {
    const parts = extractSubMeshes(sourceObject);
    this.assetRegistry.set(type, parts);
    return parts;
  }

  public getAssetParts(type: string): SubMeshPart[] | undefined {
    return this.assetRegistry.get(type);
  }

  public hasAsset(type: string): boolean {
    return this.assetRegistry.has(type);
  }

  public clearAssets(): void {
    this.assetRegistry.clear();
  }
}

export const instancedAssetManager = new InstancedAssetManagerService();
export default InstancedMultiMeshGroup;

